import { useEffect, useState } from 'react'
import type { ReservationWidget as ReservationWidgetModel } from '@sevenrooms/domain'
import { useLocales } from '@sevenrooms/locales'
import { DateOnly } from '@sevenrooms/timepiece'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { Box, VStack } from '@sevenrooms/ui-kit/layout'
import { Text } from '@sevenrooms/ui-kit/typography'
import { WidgetTitle, OtherVenueAvailability, OtherAvailabilitySkeleton } from '../../components'
import { SpecialAttention } from '../../components/PrimaryAvailability/SpecialAttention'
import {
  useReservationsRoute,
  useVenue,
  useWidgetLanguage,
  useWidgetSettings,
  type ExperienceWithAvailability,
  useOtherVenueNavigation,
} from '../../hooks'
import { reservationWidgetMessages } from '../../reservationWidgetMessages'
import type { AvailabilityTimeWithUpSellCost } from '../../hooks/useAvailability'
import type { OtherVenueInfo } from '@/domain'

interface ReservationsGroupWidgetProps {
  venuesInfo?: ReservationWidgetModel.VenueInfo
  isLoading?: boolean
  partySize: number
  startDate: Date
  startTime: string
  haloTimeIntervalMinutes: number
  duration?: number
  onTimeSlotClick: (slot: AvailabilityTimeWithUpSellCost, venue: OtherVenueInfo) => void
  showPrivateEvents?: boolean
  onPrivateEventsTimeSlotClick?: (
    slot: AvailabilityTimeWithUpSellCost,
    experience: ExperienceWithAvailability,
    venue?: OtherVenueInfo
  ) => void
  onPrivateEventsMoreDetailsClick?: (experience: ExperienceWithAvailability) => void
}
export function ReservationsGroupWidget({
  isLoading,
  venuesInfo,
  partySize,
  startDate,
  startTime,
  haloTimeIntervalMinutes,
  duration,
  onTimeSlotClick,
  showPrivateEvents,
  onPrivateEventsTimeSlotClick,
  onPrivateEventsMoreDetailsClick,
}: ReservationsGroupWidgetProps) {
  const { urlKey: venueUrlKey, currencyCode, startOfDayTime } = useVenue()
  const widgetSettings = useWidgetSettings()
  const { formatMessage } = useLocales()
  const { selectedLanguage } = useWidgetLanguage()
  const { navigateToOtherVenue } = useOtherVenueNavigation()
  const { clientId } = useReservationsRoute()
  const isMobile = useMaxWidthBreakpoint('s')

  const [venuesWithNoAvailabilities, setVenuesWithNoAvailabilities] = useState<string[]>([])

  const otherVenues = venuesInfo?.venues?.filter(v => v.urlKey !== venueUrlKey)

  useEffect(() => {
    // Need to reset when party size, time, and date changes
    setVenuesWithNoAvailabilities([])
  }, [partySize, startDate, startTime])

  return (
    <>
      <VStack width="100%" pt="lm" pb="lm" spacing="lm">
        <WidgetTitle />
        {widgetSettings.showSpecialAttentionMessage && <SpecialAttention data-test="venue-special-attention" />}
      </VStack>
      {isLoading ? (
        <VStack spacing="lm">
          <OtherAvailabilitySkeleton />
        </VStack>
      ) : (
        <>
          {venuesWithNoAvailabilities.length === otherVenues?.length ? (
            <Box p="lm">
              <Text textStyle="body1">{formatMessage(reservationWidgetMessages.resWidgetNoAvailabilityCriteria)}</Text>
            </Box>
          ) : (
            <VStack spacing="lm">
              {otherVenues?.map(v => (
                <OtherVenueAvailability
                  key={v.urlKey}
                  venue={v}
                  partySize={partySize}
                  startTime={startTime}
                  startDate={DateOnly.fromDate(startDate).toIso()}
                  defaultSearchTime={widgetSettings.defaultSearchTime}
                  duration={duration}
                  haloTimeIntervalMinutes={haloTimeIntervalMinutes}
                  currencyCode={currencyCode}
                  selectedLangCode={selectedLanguage}
                  startOfDayTime={startOfDayTime}
                  collapsed={false}
                  isMobile={isMobile}
                  onTimeSlotClick={onTimeSlotClick}
                  clientId={clientId}
                  isGroupWidget
                  showPrivateEvents={showPrivateEvents}
                  onPrivateEventsTimeSlotClick={onPrivateEventsTimeSlotClick}
                  onPrivateEventsMoreDetailsClick={onPrivateEventsMoreDetailsClick}
                  onShowMoreClick={() => navigateToOtherVenue(v.urlKey)}
                  onTitleClick={navigateToOtherVenue}
                  onNoAvailabilities={(venueId: string) => {
                    if (!venuesWithNoAvailabilities.includes(venueId)) {
                      setVenuesWithNoAvailabilities([...venuesWithNoAvailabilities, venueId])
                    }
                  }}
                  noAvailabilityMessage={formatMessage(reservationWidgetMessages.resWidgetNoAvailabilityCriteria)}
                />
              ))}
            </VStack>
          )}
        </>
      )}
    </>
  )
}
